import React from 'react';

// ─── Button ───────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type ButtonSize    = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?:    ButtonSize;
  loading?: boolean;
  icon?:    React.ReactNode;
}

const btnStyles: Record<ButtonVariant, React.CSSProperties> = {
  primary:   { background: '#3b82f6', color: '#fff',    border: '1px solid #2563eb' },
  secondary: { background: '#111927', color: '#e8edf5', border: '1px solid #1e2d42' },
  ghost:     { background: 'transparent', color: '#8a9bb5', border: '1px solid transparent' },
  danger:    { background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' },
  success:   { background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)' },
};

const sizeStyles: Record<ButtonSize, React.CSSProperties> = {
  sm: { padding: '5px 10px', fontSize: 12, gap: 5 },
  md: { padding: '8px 16px', fontSize: 13, gap: 7 },
  lg: { padding: '11px 22px', fontSize: 14, gap: 8 },
};

export function Button({
  variant = 'secondary', size = 'md', loading, icon, children,
  style, disabled, ...props
}: ButtonProps) {
  return (
    <button
      disabled={disabled || loading}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 7, fontFamily: 'Sora, sans-serif', fontWeight: 500,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s',
        opacity: disabled ? 0.5 : 1,
        whiteSpace: 'nowrap',
        ...btnStyles[variant],
        ...sizeStyles[size],
        ...style,
      }}
      {...props}
    >
      {loading ? <Spinner size={14} /> : icon}
      {children}
    </button>
  );
}

// ─── Input ────────────────────────────────────────────────────────────────────

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?:   string;
  error?:   string;
  helper?:  string;
}

export function Input({ label, error, helper, style, ...props }: InputProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && (
        <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', letterSpacing: '0.04em' }}>
          {label}
        </label>
      )}
      <input
        style={{
          background: '#0d1420', color: '#e8edf5',
          border: `1px solid ${error ? '#ef4444' : '#1e2d42'}`,
          borderRadius: 7, padding: '8px 12px', fontSize: 13,
          fontFamily: 'Sora, sans-serif', width: '100%',
          outline: 'none', transition: 'border-color 0.15s',
          ...style,
        }}
        onFocus={e => { e.currentTarget.style.borderColor = error ? '#ef4444' : '#3b82f6'; }}
        onBlur={e  => { e.currentTarget.style.borderColor = error ? '#ef4444' : '#1e2d42'; }}
        {...props}
      />
      {error  && <span style={{ fontSize: 11, color: '#ef4444' }}>{error}</span>}
      {helper && <span style={{ fontSize: 11, color: '#4a5d7a' }}>{helper}</span>}
    </div>
  );
}

// ─── Select ───────────────────────────────────────────────────────────────────

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?:   string;
  options:  { value: string; label: string }[];
}

export function Select({ label, options, style, ...props }: SelectProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && (
        <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', letterSpacing: '0.04em' }}>
          {label}
        </label>
      )}
      <select
        style={{
          background: '#0d1420', color: '#e8edf5',
          border: '1px solid #1e2d42', borderRadius: 7,
          padding: '8px 12px', fontSize: 13,
          fontFamily: 'Sora, sans-serif', width: '100%',
          outline: 'none', cursor: 'pointer',
          ...style,
        }}
        {...props}
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// ─── Textarea ─────────────────────────────────────────────────────────────────

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
}

export function Textarea({ label, style, ...props }: TextareaProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && (
        <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', letterSpacing: '0.04em' }}>
          {label}
        </label>
      )}
      <textarea
        style={{
          background: '#0d1420', color: '#e8edf5',
          border: '1px solid #1e2d42', borderRadius: 7,
          padding: '8px 12px', fontSize: 13,
          fontFamily: 'IBM Plex Mono, monospace',
          width: '100%', resize: 'vertical', minHeight: 100,
          outline: 'none', transition: 'border-color 0.15s',
          ...style,
        }}
        onFocus={e => { e.currentTarget.style.borderColor = '#3b82f6'; }}
        onBlur={e  => { e.currentTarget.style.borderColor = '#1e2d42'; }}
        {...props}
      />
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

export function Card({
  children, style, className,
}: { children: React.ReactNode; style?: React.CSSProperties; className?: string }) {
  return (
    <div
      className={className}
      style={{
        background: '#0d1420', border: '1px solid #1e2d42',
        borderRadius: 12, padding: '20px 24px',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export function Modal({
  open, onClose, title, children, width = 520,
}: {
  open:     boolean;
  onClose:  () => void;
  title:    string;
  children: React.ReactNode;
  width?:   number;
}) {
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(8,12,20,0.85)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '24px 16px',
        backdropFilter: 'blur(4px)',
        overflowY: 'auto',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="fade-in"
        style={{
          background: '#0d1420', border: '1px solid #1e2d42',
          borderRadius: 14, width: '100%', maxWidth: width,
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          display: 'flex', flexDirection: 'column',
          maxHeight: 'calc(100vh - 48px)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px', borderBottom: '1px solid #1e2d42',
          flexShrink: 0,
        }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>{title}</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', color: '#4a5d7a',
              cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 4,
            }}
          >×</button>
        </div>
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

export function Spinner({ size = 18, color = '#3b82f6' }: { size?: number; color?: string }) {
  return (
    <div style={{
      width: size, height: size,
      border: `2px solid rgba(59,130,246,0.2)`,
      borderTopColor: color,
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
      flexShrink: 0,
    }} />
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

export function EmptyState({ icon, title, description, action }: {
  icon?:        string;
  title:        string;
  description?: string;
  action?:      React.ReactNode;
}) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '60px 24px', textAlign: 'center',
    }}>
      {icon && <div style={{ fontSize: 40, marginBottom: 16, opacity: 0.4 }}>{icon}</div>}
      <h3 style={{ fontSize: 15, fontWeight: 600, color: '#8a9bb5', marginBottom: 8 }}>{title}</h3>
      {description && (
        <p style={{ fontSize: 13, color: '#4a5d7a', maxWidth: 360, marginBottom: 20 }}>
          {description}
        </p>
      )}
      {action}
    </div>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

const statusMap: Record<string, { cls: string; dot: string; label: string }> = {
  live:       { cls: 'badge-green', dot: '#10b981', label: 'Live'       },
  ready:      { cls: 'badge-blue',  dot: '#3b82f6', label: 'Ready'      },
  syncing:    { cls: 'badge-blue',  dot: '#3b82f6', label: 'Syncing'    },
  building:   { cls: 'badge-amber', dot: '#f59e0b', label: 'Building'   },
  searching:  { cls: 'badge-amber', dot: '#f59e0b', label: 'Searching'  },
  enriching:  { cls: 'badge-amber', dot: '#f59e0b', label: 'Enriching'  },
  exporting:  { cls: 'badge-amber', dot: '#f59e0b', label: 'Exporting'  },
  complete:   { cls: 'badge-green', dot: '#10b981', label: 'Complete'   },
  queued:     { cls: 'badge-gray',  dot: '#4a5d7a', label: 'Queued'     },
  paused:     { cls: 'badge-gray',  dot: '#4a5d7a', label: 'Paused'     },
  failed:     { cls: 'badge-red',   dot: '#ef4444', label: 'Failed'     },
  error:      { cls: 'badge-red',   dot: '#ef4444', label: 'Error'      },
  active:     { cls: 'badge-green', dot: '#10b981', label: 'Active'     },
  inactive:   { cls: 'badge-gray',  dot: '#4a5d7a', label: 'Inactive'   },
  installed:  { cls: 'badge-green', dot: '#10b981', label: 'Installed'  },
  pending:    { cls: 'badge-amber', dot: '#f59e0b', label: 'Pending'    },
};

export function StatusBadge({ status }: { status: string }) {
  const s = statusMap[status] ?? { cls: 'badge-gray', dot: '#4a5d7a', label: status };
  return (
    <span className={`badge ${s.cls}`}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: s.dot, display: 'inline-block',
        animation: ['searching','enriching','exporting','syncing','building'].includes(status)
          ? 'pulse 1.5s ease-in-out infinite' : 'none',
      }} />
      {s.label}
    </span>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

export function StatCard({ label, value, sub, color = '#3b82f6' }: {
  label: string; value: string | number; sub?: string; color?: string;
}) {
  return (
    <Card style={{ padding: '18px 20px' }}>
      <div style={{ fontSize: 11, color: '#4a5d7a', fontWeight: 600,
                    letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color, fontFamily: 'IBM Plex Mono, monospace',
                    letterSpacing: '-0.02em', lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: '#4a5d7a', marginTop: 6 }}>{sub}</div>}
    </Card>
  );
}

// ─── Table ────────────────────────────────────────────────────────────────────

export function Table({ headers, children, rows }: {
  headers:   string[];
  children?: React.ReactNode;
  rows?:     React.ReactNode[][];
}) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {headers.map(h => (
              <th key={h} style={{
                padding: '10px 16px', textAlign: 'left',
                fontSize: 11, fontWeight: 600, color: '#4a5d7a',
                letterSpacing: '0.06em', textTransform: 'uppercase',
                borderBottom: '1px solid #1e2d42',
                whiteSpace: 'nowrap',
              }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows
            ? rows.map((row, ri) => (
                <tr key={ri} style={{ borderBottom: '1px solid #111927' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#111927'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  {row.map((cell, ci) => (
                    <td key={ci} style={{ padding: '12px 16px', fontSize: 13, color: '#e8edf5', verticalAlign: 'middle' }}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))
            : children
          }
        </tbody>
      </table>
    </div>
  );
}

export function Tr({ children, onClick }: {
  children: React.ReactNode; onClick?: () => void;
}) {
  return (
    <tr
      onClick={onClick}
      style={{
        borderBottom: '1px solid #111927',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background 0.12s',
      }}
      onMouseEnter={e => { if (onClick) (e.currentTarget as HTMLElement).style.background = '#111927'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
    >
      {children}
    </tr>
  );
}

export function Td({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return (
    <td style={{
      padding: '12px 16px', fontSize: 13, color: '#e8edf5',
      fontFamily: mono ? 'IBM Plex Mono, monospace' : 'inherit',
      verticalAlign: 'middle',
    }}>
      {children}
    </td>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────

export function SectionHeader({ title, subtitle, sub, action }: {
  title:     string;
  subtitle?: string;
  sub?:      string;
  action?:   React.ReactNode;
}) {
  const description = subtitle ?? sub;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start',
      justifyContent: 'space-between', marginBottom: 24, gap: 16,
    }}>
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, letterSpacing: '-0.02em' }}>
          {title}
        </h2>
        {description && (
          <p style={{ fontSize: 13, color: '#4a5d7a', marginTop: 4 }}>{description}</p>
        )}
      </div>
      {action}
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────

export function Toast({ message, type = 'success', onClose }: {
  message: string;
  type?:   'success' | 'error' | 'info';
  onClose: () => void;
}) {
  const colors = {
    success: { bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.3)', icon: '✓', color: '#10b981' },
    error:   { bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.3)',  icon: '✕', color: '#ef4444' },
    info:    { bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)', icon: 'ℹ', color: '#3b82f6' },
  }[type];

  return (
    <div className="fade-in" style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      background: '#0d1420', border: `1px solid ${colors.border}`,
      borderRadius: 10, padding: '12px 18px',
      display: 'flex', alignItems: 'center', gap: 10,
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      maxWidth: 380, fontSize: 13,
    }}>
      <span style={{ color: colors.color, fontWeight: 700, fontSize: 15 }}>{colors.icon}</span>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{
        background: 'none', border: 'none', color: '#4a5d7a',
        cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 2,
      }}>×</button>
    </div>
  );
}

// ─── Aliases for component compatibility ────────────────────────────────────

export const Btn = Button;
export const Empty = EmptyState;

export function Badge({ label }: { label: string }) {
  return <StatusBadge status={label} />;
}

export function Stat({ label, value, accent, sub }: {
  label:   string;
  value:   string | number;
  accent?: boolean;
  sub?:    string;
}) {
  return <StatCard label={label} value={value} sub={sub} color={accent ? '#10b981' : '#3b82f6'} />;
}

// ─── useToast hook ────────────────────────────────────────────────────────────

import { useState, useCallback } from 'react';

export function useToast() {
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const show = useCallback((message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const ToastEl = toast ? (
    <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
  ) : null;

  return { show, ToastEl };
}
